/**
 * Live password-strength checklist. Mirrors the rules in
 * backend/utils/passwordPolicy.js — kept in sync by hand because
 * we don't have a shared types package across the JS/Node split.
 *
 * Usage:
 *   <PasswordStrengthHint value={form.password} />
 *
 * Renders a small list under the password input. Each rule starts
 * grey + bullet, flips to green + checkmark as the user types
 * something that satisfies it. Empty input shows the full grey
 * list so the user knows what's expected before typing.
 *
 * Pure presentational — does NOT block submit. Server-side validation
 * in routes/auth.js is the authoritative gate; this is just UX.
 */

const RULES = [
  { id: 'length',    test: (v) => v.length >= 10,         label: 'At least 10 characters' },
  { id: 'lowercase', test: (v) => /[a-z]/.test(v),         label: 'At least one lowercase letter' },
  { id: 'uppercase', test: (v) => /[A-Z]/.test(v),         label: 'At least one uppercase letter' },
  { id: 'digit',     test: (v) => /\d/.test(v),            label: 'At least one number' },
  { id: 'special',   test: (v) => /[^A-Za-z0-9]/.test(v),  label: 'At least one special character' },
];

export default function PasswordStrengthHint({ value }) {
  const v = typeof value === 'string' ? value : '';
  return (
    <ul style={{
      listStyle: 'none',
      padding: 0,
      margin: '8px 0 0',
      fontSize: 12,
      lineHeight: 1.6,
    }}>
      {RULES.map((rule) => {
        const ok = rule.test(v);
        return (
          <li
            key={rule.id}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              color: ok ? '#059669' : 'var(--room-text-subtle, #94a3b8)',
              transition: 'color 0.15s ease',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 14, height: 14, borderRadius: '50%',
                background: ok ? '#d1fae5' : 'var(--room-border-soft, #f1f5f9)',
                color: ok ? '#059669' : 'var(--room-border, #cbd5e1)',
                fontSize: 10, fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {ok ? '✓' : '·'}
            </span>
            <span>{rule.label}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Returns true iff every rule passes. Useful as a client-side
 * pre-flight gate so the submit button can be disabled until the
 * password is strong enough — saves a round-trip on weak attempts.
 */
export function isPasswordStrong(value) {
  const v = typeof value === 'string' ? value : '';
  return RULES.every((r) => r.test(v));
}
